import {
  AlertDialog,
  Button,
  toast,
  useControllableState,
} from '@buffer-mono/popcorn'

import type { Tag } from '~publish/hooks/useTags'

import type { JSX } from 'react'

interface DeleteConfirmationDialogProps {
  tag: Tag
  open?: boolean
  onOpenChange?: (open: boolean) => void
  onDeleteConfirm: () => Promise<void> | void
  onClose?: () => void
  children?: React.ReactNode
}

export const DeleteConfirmationDialog = ({
  tag,
  onOpenChange,
  open,
  onDeleteConfirm,
  onClose,
  children,
}: DeleteConfirmationDialogProps): JSX.Element => {
  const [isOpenState, setIsOpenState] = useControllableState({
    prop: open,
    defaultProp: false,
    onChange: onOpenChange,
  })

  const handleDelete = async (): Promise<void> => {
    try {
      if (onClose) {
        onClose()
      }
      await onDeleteConfirm()
    } catch {
      toast.error('Failed to delete tag. Please try again later.')
    }
  }

  const handleCancel = (): void => {
    setIsOpenState(false)
    if (onClose) {
      onClose()
    }
  }

  return (
    <AlertDialog open={isOpenState} onOpenChange={setIsOpenState}>
      <AlertDialog.Trigger>{children}</AlertDialog.Trigger>
      <AlertDialog.Content>
        <AlertDialog.Title>Delete Tag</AlertDialog.Title>
        <AlertDialog.Description>
          You&apos;re about to delete the <strong>{tag.name}</strong> tag. This
          can&apos;t be undone. The tag will be removed from all posts that
          currently use it.
        </AlertDialog.Description>

        <AlertDialog.Actions>
          <AlertDialog.Cancel>
            <Button variant="tertiary" onClick={handleCancel}>
              Cancel
            </Button>
          </AlertDialog.Cancel>

          <Button
            variant="critical"
            onClick={() => {
              void handleDelete()
            }}
          >
            Delete Tag
          </Button>
        </AlertDialog.Actions>
      </AlertDialog.Content>
    </AlertDialog>
  )
}
